import { useState, type FormEvent } from "react";
import { CheckCircle2 } from "lucide-react";

import { Button } from "./ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";

const services = [
  "Socket & Switch Installation / Repair",
  "Electrical Wiring Installation / Repair",
  "Light Fixture Installation / Repair",
  "Outdoor Lighting Installation",
  "Electrical Panel Repair / Upgrade",
  "EV Charger Installation",
  "Fan Installation / Repair",
  "Alarm & Security System Installation",
  "Electrical Inspection (EICR)",
  "Emergency Call-Out",
  "Rewiring (Full / Partial)",
  "General Repairs & Remodelling",
  "Other",
];

export const QuoteForm = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <Card id="quote" className="scroll-mt-24 gap-6 border-white/10 bg-card shadow-xl">
      <CardHeader>
        <CardTitle className="text-xl font-bold text-primary sm:text-2xl">
          Get a Free, No-Obligation Quote
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Tell us a little about the job and we'll get back to you within one
          working day.
        </p>
      </CardHeader>
      <CardContent>
        {submitted ? (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <CheckCircle2 className="size-12 text-primary" />
            <h3 className="text-lg font-semibold">Thanks, your request is in!</h3>
            <p className="max-w-md text-sm text-muted-foreground">
              A member of the E.I.M Electrical Contractors team will be in touch
              shortly to discuss your job and arrange a time that suits you.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="firstName">First Name*</Label>
              <Input id="firstName" name="firstName" required placeholder="John" />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="lastName">Last Name*</Label>
              <Input id="lastName" name="lastName" required placeholder="Smith" />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="email">Email*</Label>
              <Input id="email" name="email" type="email" required placeholder="john@example.com" />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="phone">Phone Number*</Label>
              <Input id="phone" name="phone" type="tel" required placeholder="07123 456789" />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="postcode">Postcode*</Label>
              <Input id="postcode" name="postcode" required placeholder="FY1 1AA" />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="service">Service Needed*</Label>
              <Select name="service" required>
                <SelectTrigger id="service" className="w-full">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service} value={service}>
                      {service}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2 sm:col-span-2 lg:col-span-3">
              <Label htmlFor="message">Tell us about the job</Label>
              <Textarea
                id="message"
                name="message"
                placeholder="E.g. I need an extra socket fitted in the kitchen and a light replaced in the hallway."
                className="min-h-24"
              />
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <Button type="submit" size="lg" className="w-full sm:w-auto">
                Submit and Continue
              </Button>
            </div>
          </form>
        )}
      </CardContent>
      <CardFooter>
        <p className="text-xs text-muted-foreground">
          By submitting this form, you agree to be contacted by E.I.M Electrical
          Contractors about your enquiry. We'll never share your details with
          third parties.
        </p>
      </CardFooter>
    </Card>
  );
};
